import { useContext } from 'react';

import { type EntityATI, type TProductDataRegions as ProductDataRegions, type TProductEdition as ProductEdition, type ThirdPartyFilters } from '@atlassian/search-client';
import { type BackendExperiment, type ExperimentConfig, type FrontendExperiments, getBackendExperiment } from '@atlassian/search-experiment';

import { ApplicationModes } from '../../../../../common/constants/application-modes';
import type { BootstrapState, OnSearchCompletedCallback } from '../../../../../common/constants/bootstrap';
import { TypeFilterValueKey } from '../../../../../common/constants/filters/universal-type';
import type { NounKeys } from '../../../../../common/constants/nouns';
import { ProductKeys } from '../../../../../common/constants/products';
import type { ThirdPartyConfigsBootstrap } from '../../../../../common/constants/schemas/3p-config';
import { productKeySchema, type SearchQueryStringParams } from '../../../../../common/constants/schemas/query-params';
import { getApplicationMode } from '../../../../../common/utils/bootstrap';
import { getMultiSiteSearchInRovo } from '../../../../../common/utils/experiments';
import { isHello } from '../../../../../common/utils/is-hello';
import { DynamicBootstrapContext, StaticBootstrapContext } from '../index';

/**
 * Hi Rovodev
 * Review the below steps and share back a plan before implementing. Think about each step and the impact on code sites, and share any concerns you have about the plan or steps. The goal is to migrate the accessors in a way that minimizes risk and code changes in code sites.
 *
 * To migrate each accessor safely (single PR) without modifying existing code sites:
 * 1. Identify accessor e.g. `useFeatures`
 * 2. In ../../index.tsx
 * 		a. Add "Store" suffix to hook and a deprecated JSDOC, e.g. `useFeatures` -> `useFeaturesStore`. Ensure there is a strict typescript return type
 * 		b. Add a new hook (with the base name) e.g. `useFeatures` that is exported, and with the EXP check (serveBootstrapProviderExp)
 * 			T: use the import from (3) in accessors
 * 			F: use (2a) with the Store suffix
 * 3. In this file ./accessors:
 * 		a. Create context version of hook `useFeaturesContext`. Ensure there is a strict typescript return type
 * 		b. Add test coverage for this hook
 * 		c. (optional) Create a non-exported subfunction if the logic is complex, e.g. see useBootstrapStateContext's handling of callbacks with a merged store
 * 4. in ../../../index.tsx
 *		a. Remove barrel file export to reduce code path ambiguity
 * 5. Fix imports in code sites impacted by (4) including tests/mocking;
 *		This should be the *only* code change required in code sites
 * Now this code path is safe to migrate via FG rollout, with minimal changes in code sites!
 *
 * Note: the barrel file removals are due to eslint-error from @repo/internal/import/no-relative-barrel-file-imports
 *
 * Later cleanup (single PR):
 * 1. Replace this file into ../index.tsx and remove the Context suffix from each export
 *
 * DO NOT CHANGE ../../accessors/index.tsx
 *
 * At the end of this migration we'll also likely drop the `bootstrap` path segment entirely.
 */

export type HookReturn<T> = (T)[]

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * This is being actively migrated away from in QS-9194
 *
 * Instead use specific per-value accessors
 *
 * e.g. const [{ useCloudId, useQueryParams }] = useBootstrap()
 * -> const [cloudId] = useCloudId();
 * -> const [queryParams] = useQueryParams();
 */
export const useBootstrapContext = (): HookReturn<BootstrapState> => {
	const staticContext = useContext(StaticBootstrapContext);
	const dynamicContext = useContext(DynamicBootstrapContext);

	return [{ ...staticContext, ...dynamicContext }] satisfies HookReturn<BootstrapState>;
};

/**
 * Retrieve the current application mode from context.
 * We're intentionally not exposing a simple boolean (eg. isRovoEnabled) as that will not scale if more modes are introduced.
 */
export const useApplicationModeContext = (): ApplicationModes => {
	const staticContext = useContext(StaticBootstrapContext);
	return getApplicationMode({ isRovoEnabled: staticContext.isRovoEnabled });
};

/**
 * Retrieve linked cloud ARIs from context.
 */
export const useLinkedCloudARIsContext = (): string[] | undefined => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.linkedCloudARIs;
};

/**
 * Retrieve the initial text query param from context.
 */
export const useInitialTextQueryParamContext = (): string | undefined => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.initialTextQueryParam;
};

/**
 * Retrieve the referrer from context.
 */
export const useReferrerContext = (): string | undefined => {
	const dynamicContext = useContext(DynamicBootstrapContext);
	return dynamicContext.referrer;
};

/**
 * Retrieve whether nouns are available from context.
 */
export const useIsNounsAvailableContext = (): boolean => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.isNounsAvailable;
};

/*
 * Retrieve whether filters discoverability 2 is enabled from context.
 * Returns true if primary product is Confluence or Jira.
 */
export const useFiltersDiscoverability2EnabledContext = (): boolean => {
	const staticContext = useContext(StaticBootstrapContext);
	const primaryProduct = staticContext.primaryProduct;
	if (!primaryProduct) {
		return false;
	}
	return primaryProduct === ProductKeys.Confluence || primaryProduct === ProductKeys.Jira;
};

/**
 * Retrieve third party configs from context.
 */
export const useThirdPartyConfigsContext = (): ThirdPartyConfigsBootstrap => {
	const dynamicContext = useContext(DynamicBootstrapContext);
	return dynamicContext.thirdPartyConfigs;
};

/**
 * Retrieve product edition from context.
 */
export const useProductEditionContext = (): ProductEdition | undefined => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.productEdition;
};

/**
 * Retrieve whether Rovo is free from context.
 * Note: this hook is only temporary and should be removed once Rovo is enabled for all users.
 */
export const useIsRovoFreeContext = (): boolean => {
	const staticContext = useContext(StaticBootstrapContext);
	return !!staticContext.rovoFeatures?.scas;
};

/**
 * Retrieve the Intercom HMAC from context.
 */
export const useIntercomHmacContext = (): string | undefined => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.intercomHmac;
};

/**
 * Retrieve the experiment config from context.
 */
export const useExperimentConfigContext = (): ExperimentConfig => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.experimentConfig;
};

/**
 * Retrieve whether multi-site search in Rovo is enabled from context.
 */
export const useMultiSiteSearchInRovoContext = (): boolean => {
	const staticContext = useContext(StaticBootstrapContext);
	return getMultiSiteSearchInRovo(staticContext.experimentConfig);
};

/**
 * Retrieve whether the current tenant is Hello from context.
 */
export const useIsHelloContext = (): boolean => {
	const staticContext = useContext(StaticBootstrapContext);
	return isHello(staticContext.cloudId);
};

/**
 * Retrieve the search session ID from context.
 */
export const useSearchSessionIdContext = (): string => {
	const dynamicContext = useContext(DynamicBootstrapContext);
	return dynamicContext.searchSessionId;
};

/**
 * Retrieve available nouns from context.
 */
export const useAvailableNounsContext = (): NounKeys[] => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.nouns;
};

/**
 * Retrieve available products from context.
 */
export const useAvailableProductsContext = (): ProductKeys[] => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.products;
};

/**
 * Retrieve analytics attributes from context.
 */
export const useAnalyticsAttributesContext = (): Record<string, any> | undefined => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.analyticsAttributes;
};

/**
 * Retrieve Smart Link usage data from context.
 */
export const useSmartLinkUsageContext = (): Record<string, number> | undefined => {
	const dynamicContext = useContext(DynamicBootstrapContext);
	return dynamicContext.smartLinkUsage;
};

/**
 * Retrieve whether the bootstrap config has finished loading from context.
 */
export const useIsConfigLoadedContext = (): boolean => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.isConfigLoaded;
};

/**
 * Retrieve whether available products have been loaded from context.
 * This is the shared context accessor used by both useIsAvailableProductsLoaded and useBootstrapLoading.
 */
export const useIsAvailableProductsLoadedContext = (): boolean => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.products != null && staticContext.products.length > 0;
};

/**
 * Retrieve frontend experiments from context.
 */
export const useFrontendExperimentsContext = (): FrontendExperiments => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.experimentConfig.frontendExperiments;
};

/**
 * Retrieve the backend experiment from context.
 * Accepts entities and thirdPartyFilters as arguments, matching the store hook's selector signature.
 */
export const useBackendExperimentContext = (args: { entities: EntityATI[]; thirdPartyFilters: ThirdPartyFilters }): BackendExperiment => {
	const staticContext = useContext(StaticBootstrapContext);
	return getBackendExperiment({
		experimentConfig: staticContext.experimentConfig,
		entities: args.entities,
		productEdition: staticContext.productEdition,
		isHello: isHello(staticContext.cloudId),
		productDataRegions: staticContext.productDataRegions,
		experience: staticContext.experience,
		tenantId: staticContext.cloudId,
		isJiraIssueIndexed: staticContext.isJiraIssueIndexed,
		thirdPartyFilters: args.thirdPartyFilters,
	});
};

/**
 * Retrieve the callback to set query params from context. Does not modify the store.
 */
export const useOnSearchCompletedContext = (): OnSearchCompletedCallback => {
	const { onSearchCompleted } = useContext(StaticBootstrapContext);
	return onSearchCompleted;
}

/**
 * Retrieve query params from dynamic context.
 * QueryParams change as the user interacts with search (input, filters).
 */
export const useQueryParamsContext = (): SearchQueryStringParams => {
	const dynamicContext = useContext(DynamicBootstrapContext);
	return dynamicContext.queryParams;
};

/**
 * Retrieve the aggregator host name from static context.
 */
export const useAggregatorHostNameContext = (): string | undefined => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.aggregatorHostName;
};

/**
 * Retrieve the aggregator absolute URL from static context.
 */
export const useAggAbsoluteUrlContext = (): string | undefined => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.aggAbsoluteUrl;
}

/**
 * Retrieve all filter excluded products from static context.
 */
export const useAllFilterExcludedProductsContext = (): ProductKeys[] => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.allFilterExcludedProducts;
};

/**
 * Retrieve product data regions from static context.
 */
export const useProductDataRegionsContext = (): ProductDataRegions | undefined => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.productDataRegions;
};

/**
 * Retrieve whether Jira issues are indexed from static context.
 */
export const useIsJiraIssueIndexedContext = (): boolean => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.isJiraIssueIndexed;
};

/**
 * Retrieve initial space ARIs from static context.
 */
export const useInitialSpaceARIsContext = (): string[] => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.initialSpaceARIs;
};

/**
 * Retrieve the SAIN host name from static context.
 */
export const useSAINHostNameContext = (): string | undefined => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.SAINHostName;
}

/**
 * Retrieve whether the user has edit or view plan permission from static context.
 */
export const useHasEditOrViewPlanPermissionContext = (): boolean => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.hasEditOrViewPlanPermission ?? false;
}

/**
 * Retrieve whether Rovo is enabled from static context.
 */
export const useIsRovoEnabledContext = (): boolean => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.isRovoEnabled ?? false;
}

/**
 * Retrieve experiment layers from static context.
 */
export const useExperimentLayersContext = (): string[] => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.experimentLayers ?? [];
}

/**
 * Retrieve the current user ID from static context.
 */
export const useCurrentUserIdContext = (): string | null | undefined => {
	const staticContext = useContext(StaticBootstrapContext);
	return staticContext.user?.id;
};

/**
 * Retrieve whether the primary product should be auto-selected from context.
 * Returns the primary product key if it should be auto-selected, or null otherwise.
 */
export const useAutoSelectPrimaryProductContext = (): ProductKeys | null => {
	const staticContext = useContext(StaticBootstrapContext);
	const dynamicContext = useContext(DynamicBootstrapContext);

	const { products, primaryProduct, isRovoEnabled } = staticContext;
	const { product: currentSelectedProduct, cql: currentCql } = dynamicContext.queryParams;

	const applicationMode = getApplicationMode({ isRovoEnabled });
	const primaryProductToSelect = productKeySchema.safeParse(primaryProduct);

	const shouldAutoSelectPrimaryProduct =
		!currentSelectedProduct &&
		!currentCql &&
		products &&
		products.length > 0 &&
		applicationMode === ApplicationModes.Unified &&
		primaryProductToSelect.success &&
		primaryProductToSelect.data !== ProductKeys.Atlas;

	if (shouldAutoSelectPrimaryProduct) {
		return primaryProductToSelect.data;
	} else {
		return null;
	}
};

/**
 * Retrieve whether the type filter is set to Incident from dynamic context.
 */
export const useIsTypeFilteredToIncidentContext = (): boolean => {
	const dynamicContext = useContext(DynamicBootstrapContext);
	return dynamicContext.queryParams?.type === TypeFilterValueKey.Incident;
};
